import { useState, useMemo, useCallback } from 'react';
import {
  Badge,
  Box,
  Flex,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  useColorModeValue,
  VStack,
  useToast,
  Card,
  CardBody,
  SimpleGrid,
  Skeleton,
  SkeletonText,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  useBreakpointValue,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Button,
  Divider,
} from '@chakra-ui/react';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { motion } from 'framer-motion';
import {
  useGetPendingOrdersSubscription,
  useGetActiveOrdersSubscription,
  useUpdateOrderStatusMutation,
  Order,
  Order_By
} from '../utils/__generated__/graphql';
import { CancelModal, OrderCard, OrderState, PageHeader } from './shared';
import { useNavigate } from 'react-router-dom';
import { useUserDefaultRole, useUserId } from '@nhost/react';
import {
  FiShoppingBag,
  FiClock,
  FiCheckCircle,
  FiTruck,
  FiPackage,
  FiRefreshCw,
  FiTrendingUp,
  FiAlertCircle,
} from 'react-icons/fi';
// import { sendNotification } from 'src/utils/sendNotifications';
import { gql, useMutation } from '@apollo/client';

dayjs.extend(relativeTime);

const ACCEPT_ORDER = gql`
mutation AcceptOrder($orderId: uuid!, $orderState: String!){
  acceptOrder(orderId: $orderId, orderState: $orderState){
    success
  }
}`





// Constants
const INCLUDED_STATES = [
  OrderState.COOKING,
  OrderState.ASSIGNED,
  OrderState.SHIPPING,
  OrderState.DELIVERED,
  OrderState.CANCELED,
] as const;

const NOTIFICATION_STATES = [OrderState.ACCEPTED, OrderState.COOKING] as const;

const TAB_STORAGE_KEY = 'orderActiveTab';



interface OrderStatsProps {
  totalOrders: number;
  newOrders: number;
  pendingOrders: number;
  completedOrders: number;
}

interface EmptyStateProps {
  icon: any;
  title: string;
  description: string;
  action?: React.ReactNode;
}

// Utility functions
const getTabIcon = (status: OrderState): any => {
  const iconMap: Record<OrderState, any> = {
    [OrderState.COOKING]: FiClock,
    [OrderState.SHIPPING]: FiTruck,
    [OrderState.DELIVERED]: FiCheckCircle,
    [OrderState.ASSIGNED]: FiPackage,
    [OrderState.CANCELED]: FiAlertCircle,
    [OrderState.ACCEPTED]: FiShoppingBag,
    [OrderState.PENDING]: FiShoppingBag,
    [OrderState.COMPLETED]: FiCheckCircle,
  };
  return iconMap[status] || FiPackage;
};

const getBadgeColorScheme = (status: OrderState): string => {
  const colorMap: Record<string, string> = {
    [OrderState.COOKING]: 'orange',
    [OrderState.SHIPPING]: 'blue',
    [OrderState.DELIVERED]: 'green',
    [OrderState.ASSIGNED]: 'purple',
    [OrderState.CANCELED]: 'red',
  };
  return colorMap[status] || 'gray';
};

// Component definitions
const MotionBox = motion(Box);

// Icon wrapper to handle type issues
const IconWrapper = ({ icon: IconComponent, ...props }: { icon: any;[key: string]: any }) => {
  return <IconComponent {...props} />;
};

const OrderStats: React.FC<OrderStatsProps> = ({ totalOrders, newOrders, pendingOrders, completedOrders }) => {
  const statBg = useColorModeValue('white', 'gray.800');
  const statBorder = useColorModeValue('gray.200', 'gray.600');

  return (
    <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4} mb={6}>
      <Card bg={statBg} borderColor={statBorder} borderWidth={1}>
        <CardBody p={4}>
          <Stat>
            <StatLabel fontSize="sm" color="gray.500">
              Total Orders
            </StatLabel>
            <StatNumber fontSize="2xl" fontWeight="bold">
              {totalOrders}
            </StatNumber>
            <StatHelpText mb={0}>
              <IconWrapper icon={FiTrendingUp} color="green.500" mr={1} />
              All time
            </StatHelpText>
          </Stat>
        </CardBody>
      </Card>

      <Card bg={statBg} borderColor="red.200" borderWidth={1}>
        <CardBody p={4}>
          <Stat>
            <StatLabel fontSize="sm" color="gray.500">
              New Orders
            </StatLabel>
            <StatNumber fontSize="2xl" fontWeight="bold" color="red.500">
              {newOrders}
            </StatNumber>
            <StatHelpText mb={0}>Needs attention</StatHelpText>
          </Stat>
        </CardBody>
      </Card>

      <Card bg={statBg} borderColor="orange.200" borderWidth={1}>
        <CardBody p={4}>
          <Stat>
            <StatLabel fontSize="sm" color="gray.500">
              In Progress
            </StatLabel>
            <StatNumber fontSize="2xl" fontWeight="bold" color="orange.500">
              {pendingOrders}
            </StatNumber>
            <StatHelpText mb={0}>Being processed</StatHelpText>
          </Stat>
        </CardBody>
      </Card>

      <Card bg={statBg} borderColor="green.200" borderWidth={1}>
        <CardBody p={4}>
          <Stat>
            <StatLabel fontSize="sm" color="gray.500">
              Completed
            </StatLabel>
            <StatNumber fontSize="2xl" fontWeight="bold" color="green.500">
              {completedOrders}
            </StatNumber>
            <StatHelpText mb={0}>Today</StatHelpText>
          </Stat>
        </CardBody>
      </Card>
    </SimpleGrid>
  );
};

const EmptyState: React.FC<EmptyStateProps> = ({ icon, title, description, action }) => {
  const textColor = useColorModeValue('gray.600', 'gray.300');

  return (
    <VStack spacing={6} py={12} textAlign="center">
      <Box p={6} borderRadius="full" bg={useColorModeValue('gray.50', 'gray.700')}>
        <IconWrapper icon={icon} boxSize={12} color={textColor} opacity={0.6} />
      </Box>
      <VStack spacing={2}>
        <Text fontSize="xl" fontWeight="semibold" color={textColor}>
          {title}
        </Text>
        <Text color={textColor} opacity={0.8} maxW="md">
          {description}
        </Text>
      </VStack>
      {action}
    </VStack>
  );
};

const OrderGrid: React.FC<{
  orders: Order[];
  onCancel: (id: string) => void;
  onAccept: (id: string, state: OrderState) => void;
  onAssign: (id: string) => void;
  userRole: string;
}> = ({ orders, onCancel, onAccept, onAssign, userRole }) => {
  const columns = useBreakpointValue({ base: 1, xl: 2 });

  return (
    <SimpleGrid columns={columns} spacing={4}>
      {orders.map((order, index) => (
        <MotionBox
          key={order.id}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.2, delay: index * 0.05 }}
        >
          <OrderCard
            order={order}
            onCancel={onCancel}
            onAccept={onAccept}
            onStartAssigned={onAssign}
            userRole={userRole}
          />
        </MotionBox>
      ))}
    </SimpleGrid>
  );
};

const LoadingSkeleton: React.FC = () => (
  <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4}>
    {Array.from({ length: 6 }).map((_, index) => (
      <Card key={index}>
        <CardBody>
          <Skeleton height="20px" mb={3} />
          <SkeletonText mt={4} noOfLines={3} spacing={4} />
          <Skeleton height="40px" mt={4} />
        </CardBody>
      </Card>
    ))}
  </SimpleGrid>
);

// Custom hooks
const useLocalStorage = (key: string, initialValue: number) => {
  const [storedValue, setStoredValue] = useState(() => {
    try {
      const item = window.localStorage.getItem(key);
      return item ? Number(item) : initialValue;
    } catch (error) {
      console.warn(`Error reading localStorage key "${key}":`, error);
      return initialValue;
    }
  });

  const setValue = useCallback(
    (value: number) => {
      try {
        setStoredValue(value);
        window.localStorage.setItem(key, value.toString());
      } catch (error) {
        console.warn(`Error setting localStorage key "${key}":`, error);
      }
    },
    [key]
  );

  return [storedValue, setValue] as const;
};

const useOrderData = (userId: string | undefined) => {
  const {
    data: pendingOrders,
    loading: pendingLoading,
    error: pendingError,
  } = useGetPendingOrdersSubscription({
    skip: !userId,
  });

  const {
    data: activeOrders,
    loading: activeLoading,
    error: activeError,
  } = useGetActiveOrdersSubscription({
    variables: {
      includedStates: INCLUDED_STATES.map((state) => state.toString()),
      _is_null: true,
      order_by: [{ created_at: Order_By.Desc }],
      limit: 50,
      offset: 0,
    },
    skip: !userId,
  });

  const filteredOrders = useMemo(() => {
    const orders = (activeOrders?.order as Order[]) || [];
    return INCLUDED_STATES.reduce(
      (acc, status) => {
        acc[status] = orders.filter((order) => order?.status === status);
        return acc;
      },
      {} as Record<OrderState, Order[]>
    );
  }, [activeOrders?.order]);

  const orderCounts = useMemo(
    () =>
      Object.entries(filteredOrders).reduce(
        (acc, [status, orders]) => {
          acc[status as OrderState] = orders?.length || 0;
          return acc;
        },
        {} as Record<OrderState, number>
      ),
    [filteredOrders]
  );

  const totalOrders = useMemo(() => {
    return (pendingOrders?.order?.length || 0) + Object.values(orderCounts).reduce((a, b) => a + b, 0);
  }, [pendingOrders?.order?.length, orderCounts]);

  return {
    pendingOrders: (pendingOrders?.order as Order[]) || [],
    filteredOrders,
    orderCounts,
    totalOrders,
    loading: pendingLoading || activeLoading,
    error: pendingError || activeError,
  };
};




// Main component
const Orders = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const userRole = useUserDefaultRole();
  const userId = useUserId();

  const [activeTab, setActiveTab] = useLocalStorage(TAB_STORAGE_KEY, 0);
  const [isCancelModalOpen, setCancelModalOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null);

  // Color mode values
  const bg = useColorModeValue('gray.50', 'gray.900');

  // Fetch restaurant data
  // const { loading: restaurantLoading } = useGetMyRestaurantQuery({
  //   variables: { id: userId },
  //   skip: !userId,
  //   fetchPolicy: 'cache-first',
  // });

  // const restaurantId = restaurantData?.restaurant?.[0]?.id;

  // Fetch order data
  const {
    pendingOrders,
    filteredOrders,
    orderCounts,
    totalOrders,
    loading: ordersLoading,
    error: ordersError,
  } = useOrderData(userId);

  // Mutations
  const [updateOrderStatus, { loading: updateLoading }] = useUpdateOrderStatusMutation();
  const [acceptOrder] = useMutation(ACCEPT_ORDER);

  // Event handlers
  const handleTabChange = useCallback(
    (index: number) => {
      setActiveTab(index);
    },
    [setActiveTab]
  );

  const openCancelModal = useCallback((orderId: string) => {
    setSelectedOrderId(orderId);
    setCancelModalOpen(true);
  }, []);

  const closeCancelModal = useCallback(() => {
    setCancelModalOpen(false);
    setCancelReason('');
    setSelectedOrderId(null);
  }, []);

  const handleAcceptOrder = useCallback(
    async (orderId: string, orderState: OrderState) => {
      try {
        // const { data } = await updateOrderStatus({
        // variables: {
        //   pk_columns: { id: orderId },
        //   _set: { status: orderState },
        // },
        // });

        // console.log("data?.update_order_by_pk?.basket?", data?.update_order_by_pk?.basket)

        // if (data?.update_order_by_pk?.basket?.receiver?.notification_token_food) {

        //   sendNotification(data?.update_order_by_pk?.basket?.receiver?.notification_token_food, 'Order Accepted', 'We are preparing your order! 😊')
        // }

        const data = acceptOrder({
          variables: {
            orderId,
            orderState
            // pk_columns: { id: orderId },
            // _set: { status: orderState },
          },
        })

        console.log("DATA", data)

        toast({
          title: 'Order Updated',
          description: `Order status updated to ${orderState}`,
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      } catch (error: any) {
        console.error('Error accepting order:', error?.message);
        toast({
          title: 'Error',
          description: error?.message || 'Failed to update order status',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
    },
    [updateOrderStatus, toast]
  );

  const handleCancelModalSubmit = useCallback(async () => {
    if (!selectedOrderId) return;

    try {
      await updateOrderStatus({
        variables: {
          pk_columns: { id: selectedOrderId },
          _set: { status: OrderState.CANCELED, cancelReason },
        },
      });

      closeCancelModal();
      toast({
        title: 'Order Cancelled',
        description: 'Order has been cancelled successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error: any) {
      console.error('Error canceling order:', error?.message);
      toast({
        title: 'Error',
        description: error?.message || 'Failed to cancel order',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  }, [updateOrderStatus, selectedOrderId, cancelReason, closeCancelModal, toast]);

  const handleAssignOrder = useCallback(
    (orderId: string) => {
      navigate(`/order/${orderId}`);
    },
    [navigate]
  );

  const handleRefresh = useCallback(() => {
    window.location.reload();
  }, []);

  // Calculate stats
  const stats = useMemo(() => {
    const inProgress =
      orderCounts[OrderState.COOKING] + orderCounts[OrderState.ASSIGNED] + orderCounts[OrderState.SHIPPING];
    const completed = orderCounts[OrderState.DELIVERED];

    return {
      totalOrders,
      newOrders: pendingOrders.length,
      pendingOrders: inProgress,
      completedOrders: completed,
    };
  }, [totalOrders, pendingOrders.length, orderCounts]);

  // Loading state
  if (ordersLoading) {
    return (
      <Box bg={bg} minH="100vh" p={6}>
        <VStack spacing={6}>
          <Skeleton height="60px" width="100%" />
          <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4} width="100%">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} height="120px" />
            ))}
          </SimpleGrid>
          <LoadingSkeleton />
        </VStack>
      </Box>
    );
  }

  // Error state
  if (ordersError) {
    return (
      <Box bg={bg} minH="100vh" p={6}>
        <Alert status="error" borderRadius="md">
          <AlertIcon />
          <Box>
            <AlertTitle>Error loading orders!</AlertTitle>
            <AlertDescription>
              {ordersError.message || 'Something went wrong while fetching orders.'}
              <Button
                size="sm"
                colorScheme="red"
                variant="outline"
                ml={4}
                leftIcon={<IconWrapper icon={FiRefreshCw} />}
                onClick={handleRefresh}
              >
                Retry
              </Button>
            </AlertDescription>
          </Box>
        </Alert>
      </Box>
    );
  }

  return (
    <>
      <PageHeader
        title="Orders Dashboard"
        subtitle={`Managing ${stats.totalOrders} orders across all statuses`}
        badge={
          <Badge colorScheme="red" fontSize="sm" borderRadius="full" px={3} py={1}>
            {stats.newOrders} New
          </Badge>
        }
      />

      <OrderStats {...stats} />

      <Tabs size={{ base: 'sm', md: 'md' }} variant="soft-rounded" index={activeTab} onChange={handleTabChange} isLazy>
        <Box p={4} bg="white" borderRadius="xl" shadow="sm">
          <TabList
            gap={{ base: 2, md: 3 }}
            overflowX="auto"
            css={{
              '&::-webkit-scrollbar': { height: '6px' },
              '&::-webkit-scrollbar-track': { background: 'transparent' },
              '&::-webkit-scrollbar-thumb': {
                background: 'var(--chakra-colors-gray-300)',
                borderRadius: '3px',
              },
            }}
          >
            <Tab borderRadius="full" fontSize={{ base: 'xs', md: 'sm' }} fontWeight="medium">
              <Flex alignItems="center" gap={2}>
                <IconWrapper icon={FiShoppingBag} boxSize={{ base: 3, md: 4 }} />
                <Text>NEW</Text>
                {stats.newOrders > 0 && (
                  <Badge colorScheme="red" borderRadius="full" fontSize="2xs" minW={5}>
                    {stats.newOrders}
                  </Badge>
                )}
              </Flex>
            </Tab>

            {INCLUDED_STATES.map((status) => (
              <Tab key={status} borderRadius="full" fontSize={{ base: 'xs', md: 'sm' }} fontWeight="medium">
                <Flex alignItems="center" gap={2}>
                  <IconWrapper icon={getTabIcon(status)} boxSize={{ base: 3, md: 4 }} />
                  <Text>{status.toUpperCase()}</Text>
                  {orderCounts[status] > 0 && (
                    <Badge
                      colorScheme={NOTIFICATION_STATES.includes(status as any) ? 'red' : getBadgeColorScheme(status)}
                      borderRadius="full"
                      fontSize="2xs"
                      minW={5}
                    >
                      {orderCounts[status]}
                    </Badge>
                  )}
                </Flex>
              </Tab>
            ))}
          </TabList>
        </Box>

        <Divider />

        <TabPanels>
          <TabPanel p={6}>
            {pendingOrders.length === 0 ? (
              <EmptyState
                icon={FiShoppingBag}
                title="No New Orders"
                description="New orders will appear here when customers place them. Check back soon!"
                action={
                  <Button
                    leftIcon={<IconWrapper icon={FiRefreshCw} />}
                    onClick={handleRefresh}
                    variant="outline"
                    size="sm"
                  >
                    Refresh
                  </Button>
                }
              />
            ) : (
              <OrderGrid
                orders={pendingOrders}
                onCancel={openCancelModal}
                onAccept={handleAcceptOrder}
                onAssign={handleAssignOrder}
                userRole={userRole ?? ''}
              />
            )}
          </TabPanel>

          {INCLUDED_STATES.map((status) => (
            <TabPanel key={status} p={6}>
              {filteredOrders[status]?.length === 0 ? (
                <EmptyState
                  icon={getTabIcon(status)}
                  title={`No ${status} Orders`}
                  description={`Orders with ${status} status will appear here when available.`}
                />
              ) : (
                <OrderGrid
                  orders={filteredOrders[status]}
                  onCancel={openCancelModal}
                  onAccept={handleAcceptOrder}
                  onAssign={handleAssignOrder}
                  userRole={userRole ?? ''}
                />
              )}
            </TabPanel>
          ))}
        </TabPanels>
      </Tabs>

      <CancelModal
        isOpen={isCancelModalOpen}
        onClose={closeCancelModal}
        cancelReason={cancelReason}
        setCancelReason={setCancelReason}
        handleCancelModalSubmit={handleCancelModalSubmit}
        loading={updateLoading}
      />
    </>
  );
};

export default Orders;

// import { useState, useMemo, useCallback, ElementType } from 'react';
// import {
//   Badge,
//   Box,
//   Flex,
//   HStack,
//   Icon,
//   Tab,
//   TabList,
//   TabPanel,
//   TabPanels,
//   Tabs,
//   Text,
//   useColorModeValue,
//   VStack,
//   useToast,
// } from '@chakra-ui/react';
// import dayjs from 'dayjs';
// import relativeTime from 'dayjs/plugin/relativeTime';

// import {
//   useGetPendingOrdersSubscription,
//   useGetActiveOrdersSubscription,
//   useUpdateOrderStatusMutation,
//   Order,
//   Order_By,
//   useGetMyRestaurantQuery,
// } from '../utils/__generated__/graphql';
// import { CancelModal, OrderCard, OrderState, PageHeader, ModernCard, LoadingSpinner } from './shared';
// import { useNavigate } from 'react-router-dom';
// import { useUserDefaultRole, useUserId } from '@nhost/react';
// import { FiShoppingBag, FiClock, FiCheckCircle, FiTruck, FiPackage } from 'react-icons/fi';

// dayjs.extend(relativeTime);

// const INCLUDED_STATES = [
//   // OrderState.ACCEPTED,
//   OrderState.COOKING,
//   OrderState.ASSIGNED,
//   OrderState.SHIPPING,
//   OrderState.DELIVERED,
//   OrderState.CANCELED,
// ];

// const NOTIFICATION_STATES = [OrderState.ACCEPTED, OrderState.COOKING];

// const Orders = () => {
//   const navigate = useNavigate();
//   const toast = useToast();
//   const userRole = useUserDefaultRole();
//   const [activeTab, setActiveTab] = useState(() => {
//     const savedTab = localStorage.getItem('orderActiveTab');
//     return savedTab ? Number(savedTab) : 0;
//   });

//   const [isCancelModalOpen, setCancelModalOpen] = useState(false);
//   const [cancelReason, setCancelReason] = useState('');
//   const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null);

//   const textColor = useColorModeValue('gray.600', 'gray.300');
//   const accentColor = useColorModeValue('brand.500', 'brand.300');

//   const userId = useUserId();
//   const { data: restaurantData } = useGetMyRestaurantQuery({
//     variables: { id: userId },
//     skip: !userId,
//     fetchPolicy: 'cache-first',
//   });

//   const restaurantId = restaurantData?.restaurant?.[0]?.id;

//   const { data: pendingOrders } = useGetPendingOrdersSubscription({
//     variables: {
//       restaurantId: restaurantId,
//     },
//   });
//   const {
//     data: activeOrders,
//   } = useGetActiveOrdersSubscription({
//     variables: {
//       includedStates: INCLUDED_STATES,
//       _is_null: true,
//       order_by: [{ created_at: Order_By.Desc }],
//       limit: 30,
//       offset: 0,
//       restaurantId: restaurantId,
//     },
//   });

//   const [updateOrderStatus] = useUpdateOrderStatusMutation();

//   const openCancelModal = useCallback((orderId: string) => {
//     setSelectedOrderId(orderId);
//     setCancelModalOpen(true);
//   }, []);

//   const closeCancelModal = useCallback(() => {
//     setCancelModalOpen(false);
//     setCancelReason('');
//   }, []);

//   const handleAcceptOrder = useCallback(
//     async (orderId: string, orderState: OrderState) => {
//       try {
//         await updateOrderStatus({
//           variables: {
//             pk_columns: { id: orderId },
//             _set: { status: orderState },
//           },
//         });
//         toast({
//           title: 'Order Updated',
//           description: `Order status updated to ${orderState}`,
//           status: 'success',
//           duration: 3000,
//           isClosable: true,
//         });
//       } catch (error: any) {
//         console.error('Error accepting order:', error?.message);
//         toast({
//           title: 'Error',
//           description: error?.message || 'Failed to update order status',
//           status: 'error',
//           duration: 3000,
//           isClosable: true,
//         });
//       }
//     },
//     [updateOrderStatus, toast]
//   );

//   const handleCancelModalSubmit = useCallback(async () => {
//     try {
//       await updateOrderStatus({
//         variables: {
//           pk_columns: { id: selectedOrderId },
//           _set: { status: OrderState.CANCELED, cancelReason: cancelReason },
//         },
//       });
//       closeCancelModal();
//       toast({
//         title: 'Order Cancelled',
//         description: 'Order has been cancelled successfully',
//         status: 'success',
//         duration: 3000,
//         isClosable: true,
//       });
//     } catch (error: any) {
//       console.error('Error canceling order:', error?.message);
//       toast({
//         title: 'Error',
//         description: error?.message || 'Failed to cancel order',
//         status: 'error',
//         duration: 3000,
//         isClosable: true,
//       });
//     }
//   }, [updateOrderStatus, selectedOrderId, cancelReason, closeCancelModal, toast]);

//   const handleAssignOrder = useCallback(
//     (orderId: string) => {
//       navigate(`/order/${orderId}`);
//     },
//     [navigate]
//   );

//   // useEffect(() => {
//   //   localStorage.setItem('orderActiveTab', activeTab.toString());
//   // }, [activeTab]);

//   const handleActiveTab = useCallback(
//     (e: number) => {
//       setActiveTab(e);
//       localStorage.setItem('orderActiveTab', e.toString());
//     },
//     [setActiveTab]
//   );

//   const filteredOrders = useMemo(() => {
//     const orders = (activeOrders?.order as Order[]) || [];
//     return INCLUDED_STATES.reduce(
//       (acc, status) => {
//         acc[status] = orders.filter((order) => order?.status === status);
//         return acc;
//       },
//       {} as Record<OrderState, Order[]>
//     );
//   }, [activeOrders?.order]);

//   const orderCounts = useMemo(
//     () =>
//       Object.entries(filteredOrders).reduce(
//         (acc, [status, orders]) => {
//           acc[status as OrderState] = orders ? orders.length : 0;
//           return acc;
//         },
//         {} as Record<OrderState, number>
//       ),
//     [filteredOrders]
//   );

//   const generateOrderList = useCallback(
//     (orders: Order[] = []) => (
//       <Box
//         style={{
//           display: 'grid',
//           gridTemplateColumns: 'repeat(auto-fit, minmax(380px, 1fr))',
//           gap: '1rem',
//         }}
//       >
//         {orders.map((order: Order) => (
//           <OrderCard
//             key={order.id}
//             order={order}
//             onCancel={openCancelModal}
//             onAccept={handleAcceptOrder}
//             onStartAssigned={handleAssignOrder}
//             userRole={userRole}
//           />
//         ))}
//       </Box>
//     ),
//     [openCancelModal, handleAcceptOrder, handleAssignOrder]
//   );

//   return (
//     <>
//       <PageHeader
//         title="Orders Dashboard"
//         subtitle={`Total Orders: ${(pendingOrders?.order?.length ?? 0) + Object.values(orderCounts).reduce((a, b) => a + b, 0)}`}
//         badge={
//           <Badge colorScheme="brand" fontSize="sm" borderRadius="full" px={3} py={1}>
//             {pendingOrders?.order?.length || 0} New
//           </Badge>
//         }
//       />

//       {/* <ModernCard overflow="hidden"> */}
//       <Tabs size={{ base: 'sm', md: 'md' }} variant="soft-rounded" index={activeTab} onChange={handleActiveTab}>
//         <Box borderRadius="xl" overflow="hidden" backgroundColor={useColorModeValue('white', 'gray.800')} shadow="sm" pt={6} px={4} alignItems="center" display="flex">
//           <TabList
//             mb={{ base: 4, md: 6 }}
//             gap={{ base: 2, md: 4 }}
//             overflowX="auto"
//             css={{
//               '&::-webkit-scrollbar': { height: '8px' },
//               '&::-webkit-scrollbar-track': { background: 'transparent' },
//               '&::-webkit-scrollbar-thumb': { background: 'var(--chakra-colors-gray-300)', borderRadius: '4px' },
//               '&::-webkit-scrollbar-thumb:hover': { background: 'var(--chakra-colors-gray-400)' },
//             }}
//           >
//             <Tab borderRadius="full" fontSize={{ base: 'xs', md: 'sm' }}>
//               <Flex alignItems="center" gap={2}>
//                 <Icon as={FiShoppingBag as ElementType} boxSize={{ base: 3, md: 4 }} />
//                 <Text>NEW ORDERS</Text>
//                 {pendingOrders?.order?.length ? (
//                   <Badge colorScheme="red" borderRadius="full" fontSize="xs">
//                     {pendingOrders?.order?.length}
//                   </Badge>
//                 ) : null}
//               </Flex>
//             </Tab>

//             {INCLUDED_STATES.map((status) => (
//               <Tab key={status} borderRadius="full" fontSize={{ base: 'xs', md: 'sm' }}>
//                 <Flex alignItems="center" gap={2}>
//                   <Icon
//                     as={
//                       status === OrderState.COOKING ? FiClock as ElementType :
//                         status === OrderState.SHIPPING ? FiTruck as ElementType :
//                           status === OrderState.DELIVERED ? FiCheckCircle as ElementType :
//                             FiPackage as ElementType
//                     }
//                     boxSize={{ base: 3, md: 4 }}
//                   />
//                   <Text>{status}</Text>
//                   {NOTIFICATION_STATES.includes(status) ? (
//                     orderCounts[status] ? (
//                       <Badge colorScheme="red" borderRadius="full" fontSize="xs">
//                         {orderCounts[status]}
//                       </Badge>
//                     ) : null
//                   ) : (
//                     orderCounts[status] ? (
//                       <Badge colorScheme="gray" borderRadius="full" fontSize="xs">
//                         {orderCounts[status]}
//                       </Badge>
//                     ) : null
//                   )}
//                 </Flex>
//               </Tab>
//             ))}
//           </TabList>

//         </Box>

//         <TabPanels>
//           <TabPanel px={0}>
//             {(pendingOrders?.order as Order[])?.length === 0 ? (
//               <VStack spacing={4} py={8} textAlign="center">
//                 <Icon as={FiShoppingBag as ElementType} boxSize={12} color={textColor} opacity={0.5} />
//                 <Text fontSize="lg" fontWeight="semibold" color={textColor}>
//                   No New Orders
//                 </Text>
//                 <Text color={textColor} opacity={0.8}>
//                   New orders will appear here when customers place them.
//                 </Text>
//               </VStack>
//             ) : (
//               generateOrderList((pendingOrders?.order as Order[]) || [])
//             )}
//           </TabPanel>
//           {INCLUDED_STATES.map((status) => (
//             <TabPanel key={status} px={0}>
//               {filteredOrders[status]?.length === 0 ? (
//                 <VStack spacing={4} py={8} textAlign="center">
//                   <Icon as={FiPackage as ElementType} boxSize={12} color={textColor} opacity={0.5} />
//                   <Text fontSize="lg" fontWeight="semibold" color={textColor}>
//                     No {status} Orders
//                   </Text>
//                   <Text color={textColor} opacity={0.8}>
//                     Orders in {status} status will appear here.
//                   </Text>
//                 </VStack>
//               ) : (
//                 generateOrderList(filteredOrders[status])
//               )}
//             </TabPanel>
//           ))}
//         </TabPanels>
//       </Tabs>

//       <CancelModal
//         isOpen={isCancelModalOpen}
//         onClose={closeCancelModal}
//         cancelReason={cancelReason}
//         setCancelReason={setCancelReason}
//         handleCancelModalSubmit={handleCancelModalSubmit}
//       />
//     </>
//   );
// };

// export default Orders;

// dayjs.extend(relativeTime);

// const INCLUDED_STATES = [
//   OrderState.ACCEPTED,
//   OrderState.COOKING,
//   OrderState.ASSIGNED,
//   OrderState.SHIPPING,
//   OrderState.DELIVERED,
//   OrderState.CANCELED,
// ];

// const Orders = () => {
//   const [hasMore, setHasMore] = useState(false);
//   const [isCancelModalOpen, setCancelModalOpen] = useState(false);
//   const [cancelReason, setCancelReason] = useState('');
//   const [selectedOrder, setSelectedOrderId] = useState<Order | null>(null);

//   const { data: pendingOrders } = useGetPendingOrdersSubscription();
//   const {
//     data: activeOrders,
//     fetchMore,
//     refetch,
//   } = useGetIncludedOrdersQuery({
//     variables: {
//       includedStates: INCLUDED_STATES,
//       order_by: [{ created_at: Order_By.Desc }],
//       limit: 30,
//       offset: 0,
//     },
//   });

//   const [updateOrderStatus] = useUpdateOrderStatusMutation({
//     onCompleted: () => refetch(),
//   });

//   const loadMore = useCallback(() => {
//     fetchMore({
//       variables: {
//         offset: activeOrders?.order?.length ?? 0,
//       },
//       updateQuery: (prev, { fetchMoreResult }) => {
//         if (!fetchMoreResult || !fetchMoreResult.order.length) {
//           setHasMore(false);
//           return prev;
//         }
//         return {
//           ...prev,
//           order: [...(prev.order || []), ...fetchMoreResult.order],
//         };
//       },
//     });
//   }, [activeOrders?.order?.length, fetchMore]);

//   const openCancelModal = useCallback((order: Order) => {
//     setSelectedOrderId(order);
//     setCancelModalOpen(true);
//   }, []);

//   const closeCancelModal = useCallback(() => {
//     setCancelModalOpen(false);
//     setCancelReason('');
//   }, []);

//   const handleAcceptOrder = useCallback(
//     async (orderId: string) => {
//       try {
//         await updateOrderStatus({
//           variables: {
//             pk_columns: { id: orderId },
//             _set: { status: OrderState.ACCEPTED },
//           },
//         });
//       } catch (error: any) {
//         console.error('Error accepting order:', error?.message);
//       }
//     },
//     [updateOrderStatus]
//   );

//   const handleCancelModalSubmit = useCallback(async () => {
//     try {
//       await updateOrderStatus({
//         variables: {
//           pk_columns: { id: selectedOrder?.id },
//           _set: { status: OrderState.CANCELED, cancelReason: cancelReason },
//         },
//       });
//       closeCancelModal();
//     } catch (error: any) {
//       console.error('Error canceling order:', error?.message);
//     }
//   }, [updateOrderStatus, selectedOrder, cancelReason, closeCancelModal]);

//   const filteredOrders = useMemo(() => {
//     const orders = (activeOrders?.order as Order[]) || [];
//     return INCLUDED_STATES.reduce((acc, status) => {
//       acc[status] = orders.filter((order) => order?.status === status);
//       return acc;
//     }, {} as Record<OrderState, Order[]>);
//   }, [activeOrders?.order]);

//   const orderCounts = useMemo(
//     () =>
//       Object.entries(filteredOrders).reduce((acc, [status, orders]) => {
//         acc[status as OrderState] = orders ? orders.length : 0;
//         return acc;
//       }, {} as Record<OrderState, number>),
//     [filteredOrders]
//   );

//   const generateOrderList = useCallback(
//     (orders: Order[] = [], orderCardFor: 'PENDING' | 'ACTIVE') => (
//       <InfiniteScroll
//         pageStart={0}
//         loadMore={loadMore}
//         hasMore={hasMore}
//         loader={<div key={0}>Loading ...</div>}
//         style={{
//           display: 'grid',
//           gridTemplateColumns: orderCardFor == 'PENDING' ? 'repeat(1, 1fr)' : 'repeat(2, 1fr)',
//           gap: 32,
//         }}
//       >
//         {orders.map((order: Order) => (
//           <OrderCard
//             key={order.id}
//             orderCardFor={orderCardFor}
//             order={order}
//             onCancel={openCancelModal}
//             onAccept={handleAcceptOrder}
//             refetch={refetch}
//           />
//         ))}
//       </InfiniteScroll>
//     ),
//     [loadMore, hasMore, openCancelModal, handleAcceptOrder, refetch]
//   );

//   return (
//     <>
//       <Container maxW="full" p={{ base: 5, md: 10 }}>
//         <Flex justifyContent="space-between">
//           <Heading as="h3" size="lg" mb={4}>
//             Orders
//           </Heading>
//         </Flex>

//         <Box>
//           <Tabs size="md" variant="enclosed-colored" colorScheme="teal">
//             <TabList mb={4} borderBottom="2px" borderBottomColor="teal.500">
//               <Tab _selected={{ color: 'white', bg: 'teal.500' }}>NEW ORDERS </Tab>
//               {INCLUDED_STATES.filter((state) => state !== OrderState.ACCEPTED).map((status) => (
//                 <Tab key={status} _selected={{ color: 'white', bg: 'teal.500' }}>
//                   {status} ({orderCounts[status] || 0})
//                 </Tab>
//               ))}
//             </TabList>
//             <TabPanels>
//               <TabPanel>
//                 <Flex gap={8} flexDirection={{ base: 'column', xl: 'row' }}>
//                   <Box flex="1">
//                     <Text fontSize="2xl" as="b">
//                       New Orders ({pendingOrders?.order?.length || 0})
//                     </Text>
//                     {generateOrderList((pendingOrders?.order as Order[]) || [], 'PENDING')}
//                   </Box>
//                   <Box flex="2">
//                     <Text fontSize="2xl" as="b">
//                       Active Orders ({orderCounts[OrderState.ACCEPTED] || 0})
//                     </Text>
//                     {generateOrderList(filteredOrders[OrderState.ACCEPTED], 'ACTIVE')}
//                   </Box>
//                 </Flex>
//               </TabPanel>
//               {INCLUDED_STATES.filter((state) => state !== OrderState.ACCEPTED).map((status) => (
//                 <TabPanel key={status}>{generateOrderList(filteredOrders[status], 'ACTIVE')}</TabPanel>
//               ))}
//             </TabPanels>
//           </Tabs>
//         </Box>
//       </Container>

//       <CancelModal
//         isOpen={isCancelModalOpen}
//         onClose={closeCancelModal}
//         cancelReason={cancelReason}
//         setCancelReason={setCancelReason}
//         handleCancelModalSubmit={handleCancelModalSubmit}
//       />
//     </>
//   );
// };

// export default Orders;
